import { useCallback } from 'react';
import { Link } from 'react-router';
import { DataTable } from '@london-life/ui/admin/data-table';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { StatCard } from '@london-life/ui/admin/stat-card';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useAdmin } from '../lib/admin-context';
import { useRequest } from '../lib/use-request';
import { when } from '../lib/format';

const none = async () => null;

// the landing page: a summary of what this admin can see; more cards arrive with each feature
export function OverviewPage() {
  const { admin, can } = useAdmin();
  const manage = can('admins.manage');
  const audited = can('audit.read');
  const admins = useRequest(useCallback(() => (manage ? api.admin.listAdmins() : none()), [manage]));
  const roles = useRequest(useCallback(() => (manage ? api.admin.listRoles() : none()), [manage]));
  const audit = useRequest(useCallback(() => (audited ? api.admin.audit({ limit: 5 }) : none()), [audited]));
  const active = admins.data?.admins.filter((item) => !item.disabledAt).length;

  return (
    <section>
      <PageHeader
        title={`Welcome, ${admin.user.displayName ?? admin.user.email ?? 'admin'}`}
        description="A summary of London Life. Players, content and the economy appear here as those features are built."
      />
      {manage && (
        <div className="admin-stats">
          <StatCard label="Active admins" value={active ?? '…'} hint={`${admins.data?.admins.length ?? 0} in total`} />
          <StatCard label="Roles" value={roles.data?.roles.length ?? '…'} />
        </div>
      )}
      {audited && (
        <div className="admin-panel">
          <h2>Latest activity</h2>
          {audit.error && <ErrorNotice error={audit.error} onRetry={() => void audit.reload()} />}
          <DataTable
            rows={audit.data?.entries ?? null}
            loading={audit.busy}
            rowKey={(entry) => entry.id}
            empty="No activity yet."
            columns={[
              {
                key: 'when',
                header: 'When',
                render: (entry) => when(entry.createdAt),
              },
              { key: 'action', header: 'Action', render: (entry) => entry.action },
            ]}
          />
          <Link to="/audit" className="admin-muted">
            See the full audit log
          </Link>
        </div>
      )}
      {!manage && !audited && (
        <div className="admin-panel">
          <p>Your roles do not include any sections that are built yet. They will appear in the menu as they arrive.</p>
        </div>
      )}
    </section>
  );
}
